import React, { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconWrapper } from 'components/Icon';
import { SUPPORTED_LOCALES, LOCALE_LABELS } from 'i18n/config';

// On routes with a `/:lang` URL segment (the website section, public profiles) switching
// navigates to the equivalent path under the new locale prefix, so the
// choice stays shareable/bookmarkable. On routes with no such segment
// (e.g. /auth/*) there's nothing to rewrite in the URL, so it just calls
// i18n.changeLanguage() directly and the page re-renders in place.
export const LanguageSwitcher = ({ sx }) => {
  const { lang: urlLang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentLang = urlLang || i18n.language;

  const switchTo = (targetLang) => {
    setAnchorEl(null);
    if (targetLang === currentLang) return;
    if (urlLang) {
      const newPath = location.pathname.replace(new RegExp(`^/${urlLang}`), `/${targetLang}`);
      navigate(`${newPath}${location.search}`);
    } else {
      i18n.changeLanguage(targetLang);
    }
  };

  return (
    <Box sx={sx}>
      <IconWrapper
        icon="globe"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          cursor: 'pointer',
          color: 'inherit',
          svg: { height: 24, width: 24, strokeWidth: 2 },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <MenuItem key={locale} selected={locale === currentLang} onClick={() => switchTo(locale)}>
            {LOCALE_LABELS[locale]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
